/**
 * Centralized Logger Utility
 * 
 * Provides consistent logging across the application.
 * Currently outputs to console, but designed to be easily extended
 * to external services (Sentry, Datadog, etc.) in the future.
 * 
 * Usage:
 *   import logger from 'services/Logger';
 *   logger.error('ComponentName', 'Something failed', { userId, chatId });
 *   logger.warn('ComponentName', 'Unexpected state', { data });
 *   logger.info('ComponentName', 'User action', { action });
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Formats context object for logging
 */
const formatContext = (context) => {
  if (!context || Object.keys(context).length === 0) return '';
  try {
    return JSON.stringify(context, null, 2);
  } catch {
    return '[Unable to stringify context]';
  }
};

/**
 * Gets timestamp for log entries
 */
const getTimestamp = () => new Date().toISOString();

const logger = {
  /**
   * Log an error - for bugs and failures that break functionality
   * @param {string} component - Component or module name where error occurred
   * @param {string} message - Human-readable error description
   * @param {object} context - Additional context (IDs, state, etc.)
   */
  error: (component, message, context = {}) => {
    const timestamp = getTimestamp();
    const formattedContext = formatContext(context);
    
    // Always log errors (even in production for now)
    console.error(
      `[ERROR] [${timestamp}] [${component}] ${message}`,
      formattedContext ? `\nContext: ${formattedContext}` : ''
    );
    
    // TODO: Add Sentry/external service integration here
    // if (!isDevelopment) {
    //   Sentry.captureException(new Error(message), { extra: context });
    // }
  },

  /**
   * Log a warning - for unexpected but recoverable situations
   * @param {string} component - Component or module name
   * @param {string} message - Warning description
   * @param {object} context - Additional context
   */
  warn: (component, message, context = {}) => {
    const timestamp = getTimestamp();
    const formattedContext = formatContext(context);
    
    // Only log warnings in development for now
    if (isDevelopment) {
      console.warn(
        `[WARN] [${timestamp}] [${component}] ${message}`,
        formattedContext ? `\nContext: ${formattedContext}` : ''
      );
    }
    
    // TODO: Add external service integration for production warnings
  },

  /**
   * Log info - for important business events useful in debugging flows
   * @param {string} component - Component or module name
   * @param {string} message - Info description
   * @param {object} context - Additional context
   */
  info: (component, message, context = {}) => {
    // Only log info in development
    if (isDevelopment) {
      const timestamp = getTimestamp();
      const formattedContext = formatContext(context);
      
      console.info(
        `[INFO] [${timestamp}] [${component}] ${message}`,
        formattedContext ? `\nContext: ${formattedContext}` : ''
      );
    }
  },

  /**
   * Log debug - for verbose debugging, development only
   * @param {string} component - Component or module name
   * @param {string} message - Debug message
   * @param {object} context - Additional context
   */
  debug: (component, message, context = {}) => {
    if (isDevelopment) {
      const timestamp = getTimestamp();
      const formattedContext = formatContext(context);
      
      console.log(
        `[DEBUG] [${timestamp}] [${component}] ${message}`,
        formattedContext ? `\nContext: ${formattedContext}` : ''
      );
    }
  },
};

export default logger;
